import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Confirm, Pill } from "@/components/Common";
import { Field } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageTitle, Panel } from "./AdminLayout";

function Waivers() {
  const [items, setItems] = useState([]);
  const [v, setV] = useState({ title: "", content: "" });
  const load = () => api.get("/admin/waivers").then((r) => {
    setItems(r.data.items);
    const a = r.data.items.find((w) => w.active);
    if (a) setV({ title: a.title, content: a.content });
  });
  useEffect(() => {
    load();
  }, []);
  const publish = async () => {
    try {
      await api.post("/admin/waivers", v);
      toast.success("Versi waiver baru aktif. Peserta wajib menyetujui ulang.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Waiver">
      <div className="mb-3 space-y-1">
        {items.map((w) => (
          <div key={w.id} className="flex items-center gap-2 text-sm" data-testid={`waiver-version-${w.version}`}>
            <span className="font-mono font-bold">v{w.version}</span> {w.title} {w.active && <Pill className="bg-green-100 text-green-900">aktif</Pill>}
            <span className="text-xs text-muted-foreground">· {w.acceptances} persetujuan · {fmtDateTime(w.created_at)}</span>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <Field label="Judul"><Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="waiver-title-input" /></Field>
        <Field label="Isi waiver"><Textarea rows={8} value={v.content} onChange={(e) => setV({ ...v, content: e.target.value })} data-testid="waiver-content-input" /></Field>
        <Confirm testid="publish-waiver" title="Terbitkan versi waiver baru?" desc="Gunakan hanya untuk perubahan material. Semua peserta harus menyetujui ulang sebelum berpartisipasi." confirmText="Terbitkan" onConfirm={publish}
          trigger={<Button className="rounded-full" data-testid="publish-waiver-button">Terbitkan versi baru</Button>} />
      </div>
    </Panel>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [groups, setGroups] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      setGroups(r.data.age_groups.map((g) => `${g.label}:${g.min}-${g.max}`).join("\n"));
    });
  }, []);
  const save = async () => {
    try {
      const age_groups = groups.split("\n").filter(Boolean).map((l) => {
        const [label, range] = l.split(":");
        const [min, max] = range.split("-").map(Number);
        return { label: label.trim(), min, max };
      });
      setS((await api.put("/admin/settings", { minor_age_threshold: Number(s.minor_age_threshold), prepare_threshold: Number(s.prepare_threshold), require_child_confirmation: s.require_child_confirmation, age_groups })).data);
      toast.success("Pengaturan disimpan.");
    } catch (e) {
      toast.error(e.response ? errMsg(e) : "Format kelompok umur: Label:min-max per baris.");
    }
  };
  if (!s) return null;
  return (
    <div className="max-w-4xl space-y-4">
      <PageTitle title="Pengaturan" desc="Pengaturan sistem global." />
      <Panel title="Aturan komunitas">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Batas umur anak (di bawah X tahun)"><Input type="number" value={s.minor_age_threshold} onChange={(e) => setS({ ...s, minor_age_threshold: e.target.value })} data-testid="settings-minor-age-input" /></Field>
          <Field label="Popup 'giliran dekat' saat ≤ N tiket di depan"><Input type="number" value={s.prepare_threshold} onChange={(e) => setS({ ...s, prepare_threshold: e.target.value })} data-testid="settings-prepare-input" /></Field>
          <Field label="Durasi rotasi standar"><Input value={`${s.standard_rotation_minutes} menit (default jalur baru)`} disabled /></Field>
          <label className="flex items-center gap-2 pt-6 text-sm"><Switch checked={s.require_child_confirmation} onCheckedChange={(c) => setS({ ...s, require_child_confirmation: c })} data-testid="settings-child-confirm-switch" /> Wajib konfirmasi pemahaman dari anak</label>
          <div className="md:col-span-2"><Field label="Kelompok umur (Label:min-max per baris)"><Textarea rows={6} value={groups} onChange={(e) => setGroups(e.target.value)} className="font-mono text-sm" data-testid="settings-age-groups-input" /></Field></div>
        </div>
        <Button className="mt-4 rounded-full" onClick={save} data-testid="settings-save-button">Simpan pengaturan</Button>
      </Panel>
      <Waivers />
    </div>
  );
}
